import { JSONCompatible, JSONValue, toSerializableObject } from '@direct-frontend/stdlib';

import { ErrorOptions } from '../types/ErrorOptions';
import { createRandomId } from '../helpers/createRandomId';
import { createErrorInstanceOf } from '../errors/helpers/createErrorInstanceOf';
import { createErrorMarker } from './helpers/createErrorMarker';
import { formatMessage } from './helpers/formatMessage';
import { isNativeError } from './helpers/isNativeError';
import { patchError } from '../instrumentation/error';

export abstract class BaseError<
    Additional extends JSONCompatible<Additional> = JSONValue,
    GroupBy extends Record<string, string> | undefined = Record<string, string> | undefined,
> extends Error {
    // Имя конструктора(класса) ошибки. Нужно указывать для обхода минификации.
    public abstract override readonly name: string;

    // Сообщение об ошибке, как было передано в конструктор.
    public readonly originalMessage: string;

    // Уникальный ID ошибки для трекинга. Генерируется автоматически в конструкторе.
    public trackId: string;

    // Предыдущая ошибка в цепочке ошибок
    public override cause?: JSONValue;

    // Мета-информация об ошибке
    public readonly additional: Additional | undefined;

    // Используется для группировки ошибок
    public readonly groupBy: GroupBy | undefined;

    // Конструктор ошибки
    public constructor(message: string, options: ErrorOptions<Additional, GroupBy>) {
        const { cause, additional, groupBy } = options;

        super(formatMessage(message, additional));

        this.originalMessage = message;
        this.trackId = cause instanceof BaseError ? cause.trackId : createRandomId();
        this.additional = additional;
        this.cause = serializeError(cause);
        this.stack = fixNameInStack(this.stack, new.target.name, this.message);
        this.groupBy = groupBy;

        const error = {
            name: new.target.name,
            message: this.message,
            originalMessage: this.originalMessage,
            trackId: this.trackId,
            additional: this.additional,
            cause: this.cause,
            stack: this.stack,
            groupBy: this.groupBy,
        };

        Object.assign(error, { [createErrorMarker('BaseError')]: true });

        return error satisfies JSONValue;
    }
}

Object.defineProperty(BaseError, Symbol.hasInstance, {
    value: createErrorInstanceOf('BaseError'),
    configurable: true,
});

export function serializeError(error: unknown): JSONValue {
    if (error instanceof BaseError) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error Модульные ошибки на самом деле уже сериализованы
        return error;
    }

    if (isNativeError(error)) {
        return {
            [createErrorMarker('NativeError')]: true,
            name: error.name,
            message: error.message,
            stack: error.stack,
            cause: serializeError(error.cause),
        };
    }

    return toSerializableObject(error);
}

export function fixNameInStack(stack: string | undefined, name: string, message: string) {
    if (!stack) {
        return;
    }

    const frames = stack.split('\n');

    const framesWithoutMessage = frames[0]?.includes(message) ? frames.slice(1) : frames;

    return [`${name}: ${message}`, ...framesWithoutMessage].join('\n');
}

patchError();
